/**
 * 緯度経度のテキスト入力を解釈する。
 *
 * 自機位置を手入力する（GPSが無い／実地でない場所からのテスト）ときに、
 * Google マップ等が出す「36°14'02.1"N 139°44'38.4"E」形式や、
 * 単純な「36.233917, 139.744000」形式のどちらもそのまま貼り付けられるようにする。
 */

export interface LatLon {
  lat: number;
  lon: number;
}

// 度(°/ﾟ)・分('/′)・秒("/″)の代替記号を許容する
const DMS_RE = /(\d+(?:\.\d+)?)[°ﾟ]\s*(\d+(?:\.\d+)?)['′]\s*(\d+(?:\.\d+)?)["″]?\s*([NSEWnsew])/g;

/** `36°14'02.1"N 139°44'38.4"E` のような度分秒表記を解釈する */
export function parseDms(text: string): LatLon | null {
  const matches = [...text.matchAll(DMS_RE)];
  if (matches.length < 2) return null;

  let lat: number | null = null;
  let lon: number | null = null;
  for (const [, d, m, s, hemiRaw] of matches) {
    const hemi = hemiRaw.toUpperCase();
    const deg = Number(d) + Number(m) / 60 + Number(s) / 3600;
    const signed = hemi === 'S' || hemi === 'W' ? -deg : deg;
    if (hemi === 'N' || hemi === 'S') lat = signed;
    else lon = signed;
  }
  if (lat === null || lon === null) return null;
  return clampOrNull(lat, lon);
}

/** `36.233917, 139.744000` のような十進表記を解釈する */
export function parseDecimal(text: string): LatLon | null {
  const m = /(-?\d+(?:\.\d+)?)\s*[,\s]\s*(-?\d+(?:\.\d+)?)/.exec(text.trim());
  if (!m) return null;
  return clampOrNull(Number(m[1]), Number(m[2]));
}

/** DMS → 十進の順で試し、どちらの形式でも受け付ける */
export function parseLatLon(text: string): LatLon | null {
  return parseDms(text) ?? parseDecimal(text);
}

function clampOrNull(lat: number, lon: number): LatLon | null {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  return { lat, lon };
}
